import { useCallback, useMemo, useState } from 'react'
import { applySessionResult, createDefaultProgress, getNextLesson, type SessionResult } from './core/progress'
import { getLessonById, LESSONS, type LessonId } from './data/lessons'
import { HomeView } from './components/HomeView'
import { PracticeView } from './components/PracticeView'
import { HomeIcon, PracticeIcon, RabbitMark, SettingsIcon } from './components/Icons'
import { SettingsPanel } from './components/SettingsPanel'
import { useStoredState } from './hooks/useStoredState'
import { DEFAULT_SETTINGS, type AppSettings } from './types/settings'

type View = 'home' | 'practice'

function createSettings(): AppSettings {
  return {
    ...DEFAULT_SETTINGS,
    keyBindings: { ...DEFAULT_SETTINGS.keyBindings },
  }
}

export default function App() {
  const [view, setView] = useState<View>('home')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [progress, setProgress] = useStoredState('morse-rabbit:progress:v1', createDefaultProgress)
  const [settings, setSettings] = useStoredState('morse-rabbit:settings:v1', createSettings)
  const initialLesson = getNextLesson(progress) ?? LESSONS[LESSONS.length - 1]
  const [selectedLessonId, setSelectedLessonId] = useState<LessonId>(initialLesson.id)

  const lesson = useMemo(() => getLessonById(selectedLessonId), [selectedLessonId])
  const lessonIndex = LESSONS.findIndex(({ id }) => id === selectedLessonId)
  const nextLessonId = LESSONS[lessonIndex + 1]?.id

  const openLesson = useCallback((lessonId: LessonId) => {
    setSelectedLessonId(lessonId)
    setView('practice')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const openNextPractice = () => {
    const next = getNextLesson(progress) ?? LESSONS[LESSONS.length - 1]
    openLesson(next.id)
  }

  const recordSession = useCallback((result: SessionResult) => {
    setProgress((current) => applySessionResult(current, result))
  }, [setProgress])

  const goHome = () => {
    setView('home')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={`app ${view === 'practice' ? 'practice-mode' : ''}`}>
      {view === 'home' && (
        <header className="app-header">
          <button className="brand" type="button" onClick={goHome} aria-label="Morse Rabbit ホーム">
            <RabbitMark size={41} />
            <span><strong>MORSE</strong><small>RABBIT</small></span>
          </button>
          <nav className="desktop-nav" aria-label="メインメニュー">
            <button className="active" type="button" onClick={goHome}><HomeIcon size={18} /> ホーム</button>
            <button type="button" onClick={openNextPractice}><PracticeIcon size={18} /> 練習</button>
          </nav>
          <button className="header-settings" type="button" onClick={() => setSettingsOpen(true)}><SettingsIcon size={19} /><span>入力設定</span></button>
        </header>
      )}

      {view === 'home' ? (
        <HomeView progress={progress} onStartLesson={openLesson} />
      ) : (
        <PracticeView
          key={lesson.id}
          lesson={lesson}
          settings={settings}
          inputEnabled={!settingsOpen}
          onOpenSettings={() => setSettingsOpen(true)}
          onExit={goHome}
          onComplete={recordSession}
          onOpenLesson={openLesson}
          nextLessonId={nextLessonId}
        />
      )}

      {view === 'home' && (
        <footer className="site-footer">
          <div className="footer-brand"><RabbitMark size={28} /><strong>MORSE RABBIT</strong></div>
          <p>練習記録と設定は、この端末のブラウザに保存されます。</p>
          <span>KEEP YOUR OWN RHYTHM.</span>
        </footer>
      )}

      {view === 'home' && (
        <nav className="mobile-nav" aria-label="モバイルメニュー">
          <button className="active" type="button" onClick={goHome}><HomeIcon /><span>ホーム</span></button>
          <button type="button" onClick={openNextPractice}><PracticeIcon /><span>練習</span></button>
          <button type="button" onClick={() => setSettingsOpen(true)}><SettingsIcon /><span>設定</span></button>
        </nav>
      )}

      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onChange={setSettings}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  )
}
